import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactCountryFlag from 'react-country-flag';
import { CountryInfo } from '../types';
import { getCountryName, isSpecialCode } from '../utils/countryMapping';
import { appConfig } from '../config';

interface CountrySelectorProps {
  className?: string;
}

const CountrySelector: React.FC<CountrySelectorProps> = ({ className = '' }) => {
  const { t } = useTranslation();
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Build and sort country list from static config
  const countries = useMemo(() => {
    const processed: CountryInfo[] = [
      ...appConfig.eur.map(code => ({ code: code.toLowerCase(), name: getCountryName(code), region: 'eur' as const })),
      ...appConfig.ame.map(code => ({ code: code.toLowerCase(), name: getCountryName(code), region: 'ame' as const })),
      ...appConfig.asi.map(code => ({ code: code.toLowerCase(), name: getCountryName(code), region: 'asi' as const })),
    ];
    return processed.sort((a, b) => t(a.name).localeCompare(t(b.name)));
  }, [t]);

  const handleCountryChange = (countryCode: string) => {
    setSelectedCountry(countryCode);
    setIsDropdownOpen(false);
    setSearchTerm(''); // Clear search when country is selected
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
    if (!isDropdownOpen) {
      setSearchTerm(''); // Clear search when opening dropdown
    }
  };

  // Filter countries based on search term
  const filteredCountries = countries.filter(country =>
    t(country.name).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!target.closest('.custom-dropdown')) {
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleRedirect = () => {
    if (!selectedCountry) {
      alert(t('countrySelector.selectCountryFirst'));
      return;
    }

    const country = countries.find(c => c.code === selectedCountry);
    if (!country) {
      alert(t('countrySelector.invalidSelection'));
      return;
    }

    // Construct URL: https://app-<list-name>.<base-uri>
    const listName = country.region; // eur, ame, or asi
    const baseUri = appConfig['base-uri'];
    const targetUrl = `https://app-${listName}.${baseUri}`;

    // Open in new window/tab
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  };

  const renderFlag = (countryCode: string) => {
    // Special handling for custom codes
    if (isSpecialCode(countryCode)) {
      return (
        <span className="special-flag" style={{
          display: 'inline-block',
          width: '20px',
          height: '15px',
          backgroundColor: '#ccc',
          marginRight: '8px',
          fontSize: '10px',
          textAlign: 'center',
          lineHeight: '15px',
          color: '#666'
        }}>
          {countryCode.toUpperCase()}
        </span>
      );
    }

    return (
      <ReactCountryFlag
        countryCode={countryCode.toUpperCase()}
        svg
        style={{
          width: '20px',
          height: '15px',
          marginRight: '8px'
        }}
        title={t(getCountryName(countryCode))}
      />
    );
  };

  return (
    // CHB : Lancelot remplace avec existants sur la plateforme si déjà implémenté niveau look & feel
    // Rend aussi ceci compatible avec mobile
    // De préférence, le dropdown et le button doivent être côtes à côtes sauf si c'est moche
    <div className={`country-selector u-row ${className}`}>

      {/* Fake dropdown */}
      <div>
        <div className="custom-dropdown" style={{ position: 'relative' }}>
          {/* select a country dropdown */}
          <div
            onClick={toggleDropdown}
            className="custom-dropdown-header"
          >
            {selectedCountry ? (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {renderFlag(selectedCountry)}
                <span style={{
                  marginLeft: 'auto',
                }}>{t(getCountryName(selectedCountry))}</span>
              </div>
            ) : (
              <span style={{ color: '#999' }}>{t('countrySelector.selectCountry')}</span>
            )}
            <span style={{ marginLeft: '10px', color: '#666' }}>
              {isDropdownOpen ? '▲' : '▼'}
            </span>
          </div>

          {/* Dropdown content (search + country)*/}
          {isDropdownOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderTop: 'none',
              maxHeight: '250px',
              zIndex: 1000,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              {/* Search box */}
              <div style={{ padding: '10px', borderBottom: '1px solid #f0f0f0' }}>
                <input
                  type="text"
                  placeholder={t('countrySelector.searchCountries')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    fontSize: '1rem',
                    outline: 'none'
                  }}
                  onClick={(e) => e.stopPropagation()} // Prevent dropdown from closing
                />
              </div>

              {/* Countries list */}
              <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((country) => (
                    <div
                      key={country.code}
                      onClick={() => handleCountryChange(country.code)}
                      style={{
                        padding: '10px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        transition: 'background-color 0.2s',
                        borderBottom: '1px solid #f0f0f0'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'white';
                      }}
                    >
                      {renderFlag(country.code)}
                      <span style={{
                        marginLeft: 'auto',
                        fontSize: '14px',
                        color: '#666',
                      }}>{t(country.name)}</span>
                    </div>
                  ))
                ) : (
                  <div style={{
                    padding: '10px',
                    textAlign: 'center',
                    color: '#999',
                    fontStyle: 'italic'
                  }}>
                    {t('countrySelector.noCountriesFound')}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <button
        onClick={handleRedirect}
        disabled={!selectedCountry}
        className={`u-btn ${!selectedCountry ? 'disabled' : ''}`}
        data-variant={`${selectedCountry ? 'primary' : ''}`}
      // onMouseEnter={(e) => {
      //   if (selectedCountry) {
      //     e.currentTarget.style.backgroundColor = '#0056b3';
      //   }
      // }}
      // onMouseLeave={(e) => {
      //   if (selectedCountry) {
      //     e.currentTarget.style.backgroundColor = '#007bff';
      //   }
      // }}
      >
        {selectedCountry ? t('countrySelector.goToPortal') : t('countrySelector.selectCountryButton')}
      </button>
    </div>
  );
};

export default CountrySelector;
